import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    DOCUMENT,
    ElementRef,
    inject,
    input,
    NgZone,
    output,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { HippoNavigationService } from '@hippo/components/navigation/navigation.service';
import type { HippoNavigationItem } from '@hippo/components/navigation/navigation.types';
import type { HippoVerticalNavigationComponent } from '@hippo/components/navigation/vertical/vertical.component';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { PermissionService } from 'app/core/services/permission.service';
import { ModuleSearchResult, searchNavigation } from './navigation-search';

/**
 * Search field for the sidebar. The field itself renders where the
 * component sits (the pinned navigation header); the result list is
 * exposed as `resultsTemplate` so the layout can place it in the scrolling
 * content area, where it replaces the module tree while a query is active.
 */
@Component({
    selector: 'navigation-search',
    templateUrl: './navigation-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, MatIconModule, MatTooltipModule, TranslocoModule],
    providers: [provideTranslocoScope('codext')],
})
export class NavigationSearchComponent {
    navigation = input<HippoNavigationItem[]>([]);
    navigationName = input.required<string>();
    activeChange = output<boolean>();

    query = signal('');
    activeIndex = signal(0);
    active = computed(() => this.query().trim().length > 0);
    results = computed(() =>
        searchNavigation(this.navigation(), this.query(), item =>
            this.isVisible(item)
        )
    );
    resultsTemplate = viewChild<TemplateRef<unknown>>('resultList');

    readonly isApple = /mac|iphone|ipad/i.test(
        (navigator as { userAgentData?: { platform?: string } }).userAgentData
            ?.platform ??
            navigator.platform ??
            ''
    );
    readonly shortcutLabel = this.isApple ? '⌘K' : 'Ctrl K';

    private searchInput =
        viewChild.required<ElementRef<HTMLInputElement>>('searchInput');
    private host = inject<ElementRef<HTMLElement>>(ElementRef);
    private document = inject(DOCUMENT);
    private router = inject(Router);
    private hippoNavigationService = inject(HippoNavigationService);
    private navigationService = inject(NavigationService);
    private permissionService = inject(PermissionService);
    private destroyed = false;

    constructor() {
        const zone = inject(NgZone);
        const destroyRef = inject(DestroyRef);
        destroyRef.onDestroy(() => (this.destroyed = true));

        // Outside the zone so the app is not change-detected on every
        // keystroke anywhere in the document.
        zone.runOutsideAngular(() => {
            const onKeydown = (event: KeyboardEvent): void => {
                if (!this.isShortcut(event)) {
                    return;
                }
                event.preventDefault();
                zone.run(() => this.focus());
            };
            this.document.addEventListener('keydown', onKeydown);
            destroyRef.onDestroy(() =>
                this.document.removeEventListener('keydown', onKeydown)
            );
        });
    }

    focus(): void {
        this.navigationComponent()?.open();
        requestAnimationFrame(() => {
            if (this.destroyed) {
                return;
            }
            const input = this.searchInput().nativeElement;
            input.focus();
            input.select();
        });
    }

    setQuery(value: string): void {
        const wasActive = this.active();
        this.query.set(value);
        this.activeIndex.set(0);
        if (wasActive !== this.active()) {
            this.activeChange.emit(this.active());
        }
    }

    clear(): void {
        this.setQuery('');
        this.searchInput().nativeElement.focus();
    }

    onKeydown(event: KeyboardEvent): void {
        const results = this.results();
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveActive(1, results.length);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActive(-1, results.length);
                break;
            case 'Enter': {
                const result = results[this.activeIndex()];
                if (result) {
                    event.preventDefault();
                    this.select(result);
                }
                break;
            }
            case 'Escape':
                event.preventDefault();
                if (this.query()) {
                    this.setQuery('');
                } else {
                    this.searchInput().nativeElement.blur();
                }
                break;
        }
    }

    select(result: ModuleSearchResult): void {
        for (const ancestor of result.ancestors) {
            if (ancestor.type === 'group') {
                ancestor.show = true;
                if (ancestor.number !== undefined) {
                    this.navigationService.setMenuOpened(ancestor.number, true);
                }
            }
        }
        this.setQuery('');

        const { item } = result;
        if (item.externalLink) {
            this.openExternal(item);
            this.closeOverlayNavigation();
            return;
        }

        item.function?.(item);
        if (!item.link) {
            return;
        }
        void this.router
            .navigate([item.link], {
                fragment: item.fragment,
                preserveFragment: item.preserveFragment ?? false,
                queryParams: item.queryParams ?? undefined,
                queryParamsHandling: item.queryParamsHandling ?? undefined,
            })
            .then(() => {
                this.closeOverlayNavigation();
                setTimeout(() => this.revealActiveItem());
            });
    }

    private isShortcut(event: KeyboardEvent): boolean {
        if (
            event.defaultPrevented ||
            event.repeat ||
            event.altKey ||
            event.key?.toLowerCase() !== 'k'
        ) {
            return false;
        }
        const modifier = this.isApple
            ? event.metaKey && !event.ctrlKey
            : event.ctrlKey && !event.metaKey;
        if (!modifier) {
            return false;
        }
        return !(event.target as HTMLElement | null)?.isContentEditable;
    }

    /**
     * Hyperlink targets are free text entered by users, so only real web
     * URLs are opened, and never with access to this window.
     */
    private openExternal(item: HippoNavigationItem): void {
        let url: URL;
        try {
            url = new URL(item.link ?? '', this.document.location.origin);
        } catch {
            return;
        }
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return;
        }
        const target = item.target || '_self';
        window.open(
            url.href,
            target,
            target === '_self' ? undefined : 'noopener,noreferrer'
        );
    }

    private closeOverlayNavigation(): void {
        const navigation = this.navigationComponent();
        if (navigation?.mode === 'over') {
            navigation.close();
        }
    }

    private moveActive(step: number, count: number): void {
        if (!count) {
            return;
        }
        const next = Math.min(
            Math.max(this.activeIndex() + step, 0),
            count - 1
        );
        this.activeIndex.set(next);
        this.navigationElement()
            ?.querySelectorAll('[data-result]')
            [next]?.scrollIntoView({ block: 'nearest' });
    }

    private revealActiveItem(): void {
        this.navigationElement()
            ?.querySelector('.hippo-vertical-navigation-item-active')
            ?.scrollIntoView({ block: 'nearest' });
    }

    private navigationElement(): Element | null {
        return this.host.nativeElement.closest('hippo-vertical-navigation');
    }

    private navigationComponent():
        HippoVerticalNavigationComponent | undefined {
        return this.hippoNavigationService.getComponent<
            HippoVerticalNavigationComponent | undefined
        >(this.navigationName());
    }

    private isVisible(item: HippoNavigationItem): boolean {
        if (item.hidden?.(item)) {
            return false;
        }
        if (!item.permissions) {
            return true;
        }
        return this.permissionService.hasPermissions(item.permissions);
    }
}
